import React, { useEffect, useState } from "react";
import { api_Routes } from "../../tools/api_Routes";
import { Helper } from "../../tools/Helper";
import {
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TablePagination,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
} from "@mui/material";
import { AddButton } from "../Button/AddButton";
import { useDispatch } from "react-redux";
import DeleteModal from "./DeleteModal";
import { setOpen } from "../../Redux/ModalReducer";
import DataTable from "./data-table";
import {Can} from "../Can";

export const EmployeesTable = () => {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(false);
  const handleClose = () => {
    setShow(false);
    dispatch(setOpen(false));
  };

  const handleShow = () => {
    setShow(true);
    dispatch(setOpen(true));
  };
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [role, setRole] = useState();
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filtersChanged, setFiltersChanged] = useState(false);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleKeywordChange = (event) => {
    setKeyword(event.target.value);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedKeyword(keyword);
      if (keyword) {
        setFiltersChanged(true);
      }
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, [keyword]);

  const getData = async () => {
    setIsLoading(true);
    // Build the query string dynamically
    const queryParams = [];
    if (debouncedKeyword) queryParams.push(`name=${debouncedKeyword}`);
    if (role && role !== "null") queryParams.push(`role=${role}`);

    const queryString = queryParams.length ? `${queryParams.join("&")}` : "";

    const { response, message } = await Helper.Get({
      url: `${api_Routes.Employees.View}?per_page=${rowsPerPage}&page=${
        currentPage + 1
      }${queryString?.length > 0 ? `&${queryString}` : ""}`,
      hasToken: true,
    });
    if (response) {
      setData(response);
    } else {
      setData([]);
    }
    setIsLoading(false);
  };

  // Effect for filter changes
  useEffect(() => {
    if (filtersChanged || shouldRefresh) {
      setCurrentPage(0);
      setFiltersChanged(false);
      setShouldRefresh(false);
    } else {
      getData();
    }
  }, [filtersChanged, currentPage, shouldRefresh, rowsPerPage]);

  return (
    <div>
      {/* Filter Section */}
      <div className="flex justify-between items-center flex-wrap">
        <div className="flex items-center flex-wrap gap-4">
          <label className="font-bold inline-block text-[#6c757d] text-[15px] text-start">
            Search :
            <input
              placeholder="Write any keyword"
              type="text"
              name="keyword"
              value={keyword}
              onChange={handleKeywordChange}
              autoComplete="off"
              className="duration-200 font-normal bg-secondary dark:bg-darkPrimary text-textColor dark:text-darkTextColor ml-2
                           border-2 input-box 
                           rounded-[0.25rem]  py-[6.75px] px-3 outline-none"
            />
          </label>
          {/* Payment Status Filter */}
          <FormControl
            className="w-52"
            sx={{ m: 1, minWidth: 120 }}
            size="small"
          >
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              className="role-select"
              id="role-select"
              value={role || null}
              onChange={(e) => {
                setRole(e.target.value);
                setFiltersChanged(true);
              }}
              label="Role"
              sx={{ textAlign: "start" }}
            >
              <MenuItem value="null">All</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="assistance">Assistance</MenuItem>
              <MenuItem value="owner">Owner</MenuItem>
            </Select>
          </FormControl>
        </div>
        <Can I="store" a="employees">
        <div className="add">
          <AddButton to="/Employees/Add" label="Add Employee" />
        </div>
        </Can>
      </div>

      {/* Table and Pagination */}
      {isLoading ? (
        <LinearProgress sx={{ marginTop: 1 }} />
      ) : data && data?.data?.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <DataTable
              data={data?.data}
              setSelectedEmployeeId={setSelectedEmployeeId}
              handleShow={handleShow}
            />
          </div>
          <div className="mt-4 flex justify-center items-center">
            <TablePagination
              page={currentPage}
              className="mt-auto"
              onPageChange={handlePageChange}
              count={data.pagination.total}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              variant="outlined"
              shape="rounded"
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </div>
          <DeleteModal
            show={show}
            handleClose={handleClose}
            selectedEmployeeId={selectedEmployeeId}
            setShouldRefresh={setShouldRefresh}
          />
        </>
      ) : (
        <TableContainer>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  There are no data yet
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};
